import { action, observable, configure, runInAction } from "mobx";
import { AuthApi } from "apis";
import ApiStore from "./ApiStore";
import { Browser } from "../core/utils/";
import { STORAGE_KEYS } from "../core/utils/constants";

configure({
  enforceActions: "observed"
});

const defaultResetFormData = {
  oldPassword: "",
  newPassword: "",
  confirmPassword: ""
};

class AuthStore {
  constructor() {
    this.api = new AuthApi();
  }
  @observable dataUrl = null;
  @observable resetPasswordFormData = defaultResetFormData;
  @observable otpData = {
    tempSessionId: null,
    maskedUserId: null,
    expiresIn: null,
    language: null,
    resendCount: null,
    resendWaitSeconds: null
  };

  @action
  setResetPasswordFormData = (data) => {
    this.resetPasswordFormData = data;
  }

  @action
  loadDefaultResetPasswordFormData = () => {
    this.resetPasswordFormData = defaultResetFormData;
  }

  /**
   * signIn
   * handle sign in action
   * 
   * @param {Object} payload : username / password object
   * @returns  boolean
   */
  @action
  async signIn(payload) {
    let user = await ApiStore.call(this.api, this.api.logon, payload);
    if (user) {
      // Check if MFA is required
      if (user.status === "MFA_REQUIRED") {
        // Store OTP data
        runInAction(() => {
          this.otpData = {
            tempSessionId: user.sessionId,
            maskedUserId: user.userId,
            expiresIn: user.expiresIn,
            language: user.language,
            resendCount: user.resendCount,
            resendWaitSeconds: user.resendWaitSeconds
          };
          localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
          sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
          this.savePresessionInfo(this.otpData);
        });
        return { mfaRequired: true };
      }
      this.saveUserInfo(user, true);
      return { success: true };
    }
    return { success: false };
  }

  @action
  async verifyOtp(payload) {
    let user = await ApiStore.call(this.api, this.api.verifyOtp, payload);
    if (user) {
      this.saveUserInfo(user, true);
      // Clear OTP data
      runInAction(() => {
        this.otpData = {
          tempSessionId: null,
          maskedUserId: null,
          expiresIn: null,
          language: null
        };
        this.clearPresessionInfo();
      });
      return true;
    }
    return false;
  }

  @action
  async resendOtp() {
    let response = await ApiStore.call(this.api, this.api.resendOtp);
    if (response) {
      // Update OTP data with new session ID and cooldown info
      runInAction(() => {
        this.otpData = {
          tempSessionId: response.sessionId,
          maskedUserId: response.userId,
          expiresIn: response.expiresIn,
          language: response.language,
          resendCount: response.resendCount,
          resendWaitSeconds: response.resendWaitSeconds
        };
        this.savePresessionInfo(this.otpData);
      });
      return { success: true };
    }
    return { success: false };
  }

  @action
  async getOtpInfo() {
    let response = await ApiStore.call(this.api, this.api.getOtpInfo);
    if (response) {
      // Update OTP data with new session ID
      runInAction(() => {
        this.otpData = {
          tempSessionId: response.sessionId,
          maskedUserId: response.userId,
          expiresIn: response.expiresIn,
          language: response.language,
          resendCount: response.resendCount,
          resendWaitSeconds: response.resendWaitSeconds
        };
        this.savePresessionInfo(this.otpData);
      });
      return true;
    }
    return false;
  }

  @action
  async changePassword(payload) {
    let response = await ApiStore.call(this.api, this.api.changePassword, payload);
    return response;
  }

  @action
  async getSystemStatus() {
    let data = await ApiStore.call(this.api, this.api.getSystemStatus);
    runInAction(() => {
      this.dataUrl = data?.info_url;
    });
    return data;
  }

  /**
   * logoff
   * handle sign off action
   * 
   * @returns  null
   */
  @action
  async logoff() {
    await ApiStore.call(this.api, this.api.logoff).finally(() => {
      Browser.clearSavedInfo();
    });
  }

  /**
   * saveUserInfo
   * save user infomation to local storage
   * 
   * @param {Object} data : user object
   * @param {boolean} rememberMe : rememberMe
   * 
   * @returns  null
   */
  saveUserInfo = (data, rememberMe) => {
    if (rememberMe) {
      localStorage.setItem(STORAGE_KEYS.SESSION_ID, data.sessionId);
      localStorage.setItem(STORAGE_KEYS.USER_ID, data.userId);
      localStorage.setItem(STORAGE_KEYS.ROLE, data.role);
    } else {
      sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, data.sessionId);
      sessionStorage.setItem(STORAGE_KEYS.USER_ID, data.userId);
      sessionStorage.setItem(STORAGE_KEYS.ROLE, data.role);
    }
  };

  savePresessionInfo = (data) => {
    sessionStorage.setItem(STORAGE_KEYS.PRESESSION_ID, data.tempSessionId);
  }

  clearPresessionInfo = () => {
    sessionStorage.removeItem(STORAGE_KEYS.PRESESSION_ID);
  }
}
export default new AuthStore();
